import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, X, FlaskConical, BookOpen, CheckCircle, ExternalLink, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

/**
 * ActivityPreviewModal - shows a programme activity as a learner will see it.
 * - Learning Lab: title / overview / learning outcomes / scenarios + tasks
 * - Course: modules & sections (with an "Open full course player" link)
 */
export default function ActivityPreviewModal({ open, onClose, activity, token }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const isLab = activity?.content_type === 'learning_lab' || !!activity?.ai_lab_id;
  const courseId = activity?.ai_course_id || activity?.content_id;
  const isAiCourse = !!activity?.ai_course_id || String(courseId || '').startsWith('ai_course_');

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      if (isLab) {
        const genId = activity.ai_lab_id || activity.content_id;
        const res = await axios.get(`${API}/programme-ai/generation/${genId}`, { headers });
        setData({ kind: 'lab', content: res.data.result || {} });
      } else if (isAiCourse && courseId) {
        // AI-generated course: content lives in ai_course_data (sections)
        const res = await axios.get(`${API}/ai/course/${courseId}`, { headers });
        const aiData = res.data.ai_course_data || res.data || {};
        setData({ kind: 'course', aiCourse: true, content: aiData });
      } else if (courseId) {
        const res = await axios.get(`${API}/courses/${courseId}`, { headers });
        setData({ kind: 'course', aiCourse: false, content: res.data || {} });
      } else if (activity?.modules && activity.modules.length > 0) {
        // Manually-built content held locally on the activity
        setData({ kind: 'course', aiCourse: false, content: { title: activity.title, modules: activity.modules } });
      } else {
        setData({ kind: 'empty', content: {} });
      }
    } catch (error) {
      console.error('Preview load failed:', error);
      // Fall back to local modules if the remote fetch fails
      if (activity?.modules && activity.modules.length > 0) {
        setData({ kind: 'course', aiCourse: false, content: { title: activity.title, modules: activity.modules } });
      } else {
        toast.error('Could not load content preview');
        setData({ kind: 'empty', content: {} });
      }
    } finally {
      setLoading(false);
    }
  }, [activity, isLab, isAiCourse, courseId]);

  useEffect(() => {
    if (open && activity) fetchContent();
  }, [open, activity, fetchContent]);

  if (!open) return null;

  const status = activity?.status || 'draft';
  const sectionsOf = (m) => m.sections || m.lessons || m.topics || m.content_blocks || [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-navigate-surface border border-zinc-800 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            {isLab ? <FlaskConical className="h-5 w-5 text-teal-400" /> : <BookOpen className="h-5 w-5 text-purple-400" />}
            <h3 className="text-lg font-semibold text-white">Activity Preview</h3>
            <Badge className={status === 'published'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'}>
              {status === 'published' ? 'Published' : 'Draft'}
            </Badge>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="activity-preview-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 border-b border-zinc-800">
          <p className="text-xs text-zinc-500">Activity</p>
          <p className="text-white font-medium">{activity?.title || 'Untitled activity'}</p>
          <p className="text-xs text-zinc-400 mt-1 capitalize">{(activity?.activity_type || '').replace('_', ' ')}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-zinc-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading preview...
            </div>
          ) : data?.kind === 'lab' ? (
            <LabPreview lab={data.content} labId={typeof activity?.content_id === 'string' && activity.content_id.startsWith('lab_') ? activity.content_id : null} />
          ) : data?.kind === 'course' ? (
            <CoursePreview course={data.content} courseId={courseId} aiCourse={data.aiCourse} sectionsOf={sectionsOf} />
          ) : (
            <div className="text-center py-16">
              <FileText className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">No content has been generated or built for this activity yet.</p>
              <p className="text-zinc-500 text-xs mt-1">Use the Generate with NAVI AI or Build Manually option to add content.</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-zinc-800">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">Close</Button>
        </div>
      </div>
    </div>
  );
}

function LabPreview({ lab, labId }) {
  const title = lab.lab_title || lab.title || 'Learning Lab';
  const overview = lab.lab_overview || lab.overview || '';
  const outcomes = lab.learning_outcomes || [];
  const scenarios = lab.scenarios || [];
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
          {overview && <p className="text-sm text-zinc-400 mt-1">{overview}</p>}
        </div>
        {labId && (
          <Link
            to={`/labs/${labId}`}
            className="flex-shrink-0 inline-flex items-center gap-1 text-sm text-navigate-gold hover:underline"
            data-testid="open-lab-player-link"
          >
            Open full lab <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
      {outcomes.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-zinc-300 mb-2">Learning Outcomes</h4>
          <ul className="space-y-1">
            {outcomes.map((o, i) => (
              <li key={i} className="text-sm text-zinc-400 flex items-start gap-2">
                <CheckCircle className="h-3.5 w-3.5 text-green-400 mt-1 flex-shrink-0" />
                {typeof o === 'string' ? o : JSON.stringify(o)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {scenarios.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-zinc-300 mb-2">Scenarios ({scenarios.length})</h4>
          <div className="space-y-3">
            {scenarios.map((s, i) => {
              const tasks = Array.isArray(s.tasks) ? s.tasks : [];
              return (
                <div key={i} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-teal-400 font-medium">Scenario {i + 1}</span>
                  </div>
                  <p className="text-white font-medium mt-1">{typeof s.title === 'string' ? s.title : JSON.stringify(s.title)}</p>
                  {s.brief && <p className="text-xs text-zinc-400 mt-1">{typeof s.brief === 'string' ? s.brief : JSON.stringify(s.brief)}</p>}
                  {(s.description || s.scenario) && (
                    <p className="text-sm text-zinc-300 mt-2">{typeof (s.description || s.scenario) === 'string' ? (s.description || s.scenario) : JSON.stringify(s.description || s.scenario)}</p>
                  )}
                  {tasks.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-zinc-400 mb-1">Tasks</p>
                      <ul className="space-y-1">
                        {tasks.map((t, ti) => (
                          <li key={ti} className="text-xs text-zinc-400 flex items-start gap-2">
                            <span className="text-teal-400">{ti + 1}.</span>
                            {typeof t === 'string' ? t : (t.task || t.title || JSON.stringify(t))}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CoursePreview({ course, courseId, aiCourse, sectionsOf }) {
  // AI courses store content as `sections`; manual/standard courses as `modules`.
  const units = aiCourse ? (course.sections || course.modules || []) : (course.modules || []);
  const unitLabel = aiCourse ? 'Section' : 'Module';
  const playerPath = aiCourse ? 'ai-learn' : 'learn';

  // `overview` may be a plain string OR a structured object (AI courses).
  const overview = course.overview;
  const overviewObj = overview && typeof overview === 'object' && !Array.isArray(overview) ? overview : null;
  const titleText = course.title || overviewObj?.title || 'Course';
  const descText = typeof course.description === 'string'
    ? course.description
    : (typeof overview === 'string' ? overview : '');

  const asList = (v) => (Array.isArray(v) ? v : (v ? [v] : []))
    .map((x) => (typeof x === 'string' ? x : (x?.title || x?.text || JSON.stringify(x))));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">{titleText}</h2>
          {descText && <p className="text-sm text-zinc-400 mt-1">{descText}</p>}
          {overviewObj?.estimated_duration && (
            <p className="text-xs text-zinc-500 mt-1">Duration: {String(overviewObj.estimated_duration)}</p>
          )}
        </div>
        {courseId && (
          <Link
            to={`/courses/${courseId}/${playerPath}`}
            className="flex-shrink-0 inline-flex items-center gap-1 text-sm text-navigate-gold hover:underline"
            data-testid="open-course-player-link"
          >
            Open full course player <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>

      {/* Structured overview details (AI courses) */}
      {overviewObj && (
        <div className="grid sm:grid-cols-2 gap-4">
          {asList(overviewObj.learning_objectives).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-1">Learning Objectives</h4>
              <ul className="space-y-1">
                {asList(overviewObj.learning_objectives).map((o, i) => (
                  <li key={i} className="text-sm text-zinc-400 flex items-start gap-2">
                    <CheckCircle className="h-3.5 w-3.5 text-green-400 mt-1 flex-shrink-0" />{o}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {asList(overviewObj.expected_outcomes).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-1">Expected Outcomes</h4>
              <ul className="space-y-1">
                {asList(overviewObj.expected_outcomes).map((o, i) => (
                  <li key={i} className="text-sm text-zinc-400 flex items-start gap-2">
                    <CheckCircle className="h-3.5 w-3.5 text-teal-400 mt-1 flex-shrink-0" />{o}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {overviewObj.target_audience && (
            <div className="sm:col-span-2">
              <h4 className="text-sm font-medium text-zinc-300 mb-1">Target Audience</h4>
              <p className="text-sm text-zinc-400">{asList(overviewObj.target_audience).join(', ')}</p>
            </div>
          )}
        </div>
      )}

      {units.length === 0 ? (
        <div className="text-center py-10">
          <FileText className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-zinc-400 text-sm">No {unitLabel.toLowerCase()}s yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {units.map((m, i) => {
            const sections = aiCourse ? [] : sectionsOf(m);
            const contentText = typeof m.content === 'string'
              ? m.content
              : (typeof m.description === 'string' ? m.description : '');
            return (
              <div key={i} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-purple-400 font-medium">{unitLabel} {i + 1}</span>
                  {sections.length > 0 && (
                    <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                      {sections.length} item{sections.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                <p className="text-white font-medium mt-1">{m.title || m.section_title || `${unitLabel} ${i + 1}`}</p>
                {contentText && (
                  <p className="text-xs text-zinc-400 mt-1 line-clamp-3 whitespace-pre-line">{contentText}</p>
                )}
                {sections.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {sections.map((s, si) => (
                      <li key={si} className="text-sm text-zinc-300 flex items-start gap-2">
                        <span className="text-zinc-500 text-xs mt-0.5">{si + 1}.</span>
                        {s.title || s.name || (typeof s === 'string' ? s : (s.type || 'Item'))}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
